import { ApolloError, FetchResult, useMutation } from '@apollo/client';
import { CAPTCHA_TOKEN_HEADER, TurnstileWidgetProps, useCaptchaChallenge } from '@kompare/captcha-react';
import { useRef, useState } from 'react';
import config from 'src/config';
import { captchaStatusOf, submitStatusOf } from '../api/apollo/captcha';
import { CREATE_SO_MUTATION } from '../api/apollo/mutations/useCreateSo.mutation';
import { CreateSoMutation, CreateSoMutationVariables } from '../api/apollo/types/types';
import {
  CaptchaPage,
  describeInput,
  newChallengeId,
  reportCaptchaRequired,
  reportSubmitOutcome,
  reportTurnstileEvent,
} from '../utils/captchaTelemetry.util';
import { i18n } from '../utils/translate.util';

export interface SubmitHandlers {
  onSuccess: (result: FetchResult<CreateSoMutation>) => void;
  /** Every failure except CAPTCHA_REQUIRED — the hook owns that branch. */
  onError?: (error: ApolloError) => void;
  /** Runs after EVERY attempt, including one that ended in a challenge (drop loaders here). */
  onSettled?: () => void;
}

export interface CaptchaState {
  /** Render the widget when true (and keep the submit button disabled — the widget resubmits). */
  required: boolean;
  /** Copy to show under the widget while a challenge is due; null otherwise. */
  message: string | null;
  widgetProps: Pick<TurnstileWidgetProps, 'siteKey' | 'onToken' | 'onEvent' | 'resetSignal'>;
}

interface Options {
  page: CaptchaPage;
}

/**
 * `createSo` with the reactive captcha contract (`@kompare/rate-limit` README,
 * "Conditional captcha"; the GraphQL flavour from the funnel-hardening playbook §5.2).
 * Ported from dzo-webshop's hook of the same name.
 *
 * Submit normally. When property-api answers `CAPTCHA_REQUIRED` (HTTP 200,
 * `extensions.statusCode 428`), the hook arms the Turnstile widget, keeps the denied
 * `variables` object and — on the widget's token — resends that very same object with
 * the token in the `x-captcha-token` header. The caller's handlers run on the retry's
 * outcome exactly as they would have on the first attempt.
 *
 * Only *creations* are challenged: property-api counts a createSo without a `soId`
 * (or with an unknown one) against the ladder, an edit of an existing SO is free. So
 * the widget can only ever appear on the first submit of a session — the pages that
 * only ever edit keep the plain `useCreateSo`.
 *
 * Why the same object: the API counts attempts, and a payload re-derived from form
 * state between denial and retry could differ (re-normalised phone, edited field),
 * making the retry a different request than the one the user was challenged for.
 *
 * A 428 on the retry itself (spent/expired/rejected token, siteverify outage) is a
 * repeat challenge: `handleStatus` bumps `resetSignal`, the widget resets, the user
 * solves again. Any OTHER failure of the retry (RATE_LIMITED / IP_BLOCKED, validation,
 * network) is terminal for that payload: the pending retry is dropped, the widget is
 * dismissed and the submit button re-enabled, so the next attempt is an explicit one.
 * Rate-limit denials reach `onError` — the global `rateLimitErrorLink` already toasts them.
 */
export function useCreateSoWithCaptcha({ page }: Options) {
  // errorPolicy 'none' is required (and stated explicitly even though it is now the
  // client default): under 'all' a CAPTCHA_REQUIRED response resolves the promise with
  // `errors` populated and the challenge branch below never runs.
  const [mutate, { loading }] = useMutation<CreateSoMutation, CreateSoMutationVariables>(CREATE_SO_MUTATION, {
    errorPolicy: 'none',
  });
  const challenge = useCaptchaChallenge();
  const pendingRef = useRef<{ variables: CreateSoMutationVariables; handlers: SubmitHandlers } | null>(null);
  const inFlightRef = useRef(false);
  const [message, setMessage] = useState<string | null>(null);
  // `useCaptchaChallenge` has no "stand down" — once armed it stays `required`. When a
  // token retry ends in a TERMINAL error (RATE_LIMITED, IP_BLOCKED, validation, network)
  // the challenge is over for that payload: we hide the widget, drop the pending payload
  // and give the button back, so the user retries explicitly (with corrected input if
  // need be) instead of every fresh token replaying the same failing request. A later
  // submit that is challenged again re-arms it (`dismissed` → false).
  const [dismissed, setDismissed] = useState(false);
  // Telemetry correlation id for the challenge in progress (see captchaTelemetry.util:
  // the RUM agent never stamps `session.id` on custom transactions). Minted on the submit
  // that gets the first CAPTCHA_REQUIRED, reused by a repeat 428 (same challenge, new
  // solve), the widget's events and the token retry; cleared when the challenge ends.
  const challengeIdRef = useRef<string | null>(null);

  const run = async (variables: CreateSoMutationVariables, handlers: SubmitHandlers, token?: string) => {
    if (inFlightRef.current) return; // one attempt at a time — no double submits
    inFlightRef.current = true;
    // Reduced to kind/length, never the value.
    const input = describeInput(variables.input?.data?.phone);
    try {
      const result = await mutate({
        variables,
        context: token ? { headers: { [CAPTCHA_TOKEN_HEADER]: token } } : undefined,
      });
      reportSubmitOutcome(page, 200, input, !!token, challengeIdRef.current ?? undefined);
      challengeIdRef.current = null;
      pendingRef.current = null;
      setMessage(null);
      handlers.onSuccess(result);
    } catch (e) {
      const error = e as ApolloError;
      const challenged = challenge.handleStatus(captchaStatusOf(error));
      if (challenged && !challengeIdRef.current) challengeIdRef.current = newChallengeId();
      // Reported after the challenge check so the denied submit carries the new id too.
      reportSubmitOutcome(page, submitStatusOf(error), input, !!token, challengeIdRef.current ?? undefined);
      if (challenged) {
        // First or repeat challenge (spent/rejected token): keep the exact payload for the
        // token retry. `handleStatus` already resets the widget on a repeat — the only
        // case where an automatic re-solve makes sense.
        pendingRef.current = { variables, handlers };
        setDismissed(false);
        reportCaptchaRequired(page, challengeIdRef.current as string, input);
        setMessage(i18n.t('common.captchaRequired'));
        return;
      }
      // Terminal failure. End the pending automatic retry: nothing a fresh token could
      // change, and looping the same request would only repeat the error toast.
      challengeIdRef.current = null;
      pendingRef.current = null;
      setMessage(null);
      if (token) setDismissed(true);
      handlers.onError?.(error);
    } finally {
      inFlightRef.current = false;
      handlers.onSettled?.();
    }
  };

  // `run` never rejects (everything is routed to the handlers), so the promise is dropped.
  const submit = (variables: CreateSoMutationVariables, handlers: SubmitHandlers) => {
    run(variables, handlers);
  };

  const onToken = (token: string) => {
    const pending = pendingRef.current;
    if (pending) run(pending.variables, pending.handlers, token);
  };

  const captcha: CaptchaState = {
    required: challenge.required && !dismissed,
    message,
    widgetProps: {
      siteKey: config.REACT_APP_TURNSTILE_SITE_KEY,
      onToken,
      onEvent: (event) => reportTurnstileEvent(page, event, challengeIdRef.current ?? undefined),
      resetSignal: challenge.resetSignal,
    },
  };

  return { submit, loading, captcha };
}
